import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../lib/apiClient';
import {
  CISA_PILLARS,
  CISA_FUNCTIONS,
  CROSS_CUTTING,
  calculatePillarScore,
  getConfigurationSteps
} from '../config/cisaMaturityModel';
import {
  SCORING_WEIGHTS,
  LEVEL_THRESHOLDS,
  getEvidenceChecksForLevel,
  calculateExpirationDate,
  isEvidenceExpired,
  getExpirationWarning
} from '../config/cisaEvidenceModel';

const STORAGE_KEY = 'cisa_maturity_assessments';
const TARGETS_STORAGE_KEY = 'cisa_maturity_targets';
const PRODUCTS_STORAGE_KEY = 'cisa_maturity_products';
const CONFIG_COMPLETIONS_STORAGE_KEY = 'cisa_config_completions';
const EVIDENCE_STORAGE_KEY = 'cisa_evidence';
const EVIDENCE_CHECKS_STORAGE_KEY = 'cisa_evidence_checks';

/**
 * Hook for managing CISA Maturity Model assessments
 *
 * Extended to support:
 * - Target levels (where you want to be)
 * - Product adoptions with status tracking
 *
 * Supports both localStorage (MVP) and backend API persistence.
 * When departmentId is provided, attempts to fetch/save to backend.
 * Falls back to localStorage for unauthenticated or no-department scenarios.
 */
export function useCisaAssessment(departmentId) {
  const queryClient = useQueryClient();

  // Current level assessments
  const [localAssessments, setLocalAssessments] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Target level assessments
  const [localTargets, setLocalTargets] = useState(() => {
    try {
      const stored = localStorage.getItem(TARGETS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Product adoptions
  const [localProducts, setLocalProducts] = useState(() => {
    try {
      const stored = localStorage.getItem(PRODUCTS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Configuration completions - tracks which config steps are done
  const [localConfigCompletions, setLocalConfigCompletions] = useState(() => {
    try {
      const stored = localStorage.getItem(CONFIG_COMPLETIONS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Evidence - stored evidence items per function
  const [localEvidence, setLocalEvidence] = useState(() => {
    try {
      const stored = localStorage.getItem(EVIDENCE_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Evidence check statuses - verification status per check ID
  const [localEvidenceChecks, setLocalEvidenceChecks] = useState(() => {
    try {
      const stored = localStorage.getItem(EVIDENCE_CHECKS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Backend API query (only enabled when departmentId is provided)
  const {
    data: backendData,
    isLoading,
    error: queryError
  } = useQuery({
    queryKey: ['cisa-assessment', departmentId],
    queryFn: () => apiClient.get(`/api/zero-trust/departments/${departmentId}/cisa-assessment`),
    enabled: !!departmentId,
    retry: false,
    staleTime: 30000
  });

  // Backend mutation
  const updateMutation = useMutation({
    mutationFn: (update) =>
      apiClient.post(`/api/zero-trust/departments/${departmentId}/cisa-assessment`, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cisa-assessment', departmentId] });
    }
  });

  // Determine if we should use backend or localStorage
  const useLocalStorage = !departmentId || queryError?.status === 404 || queryError;

  // Get storage key based on departmentId
  const storageKey = departmentId || 'global';

  // Merge assessments: backend takes precedence if available
  const assessments = useMemo(() => {
    if (!useLocalStorage && backendData?.assessments) {
      return backendData.assessments;
    }
    return localAssessments[storageKey] || {};
  }, [backendData, localAssessments, storageKey, useLocalStorage]);

  // Get target levels
  const targetLevels = useMemo(() => {
    if (!useLocalStorage && backendData?.targetLevels) {
      return backendData.targetLevels;
    }
    return localTargets[storageKey] || {};
  }, [backendData, localTargets, storageKey, useLocalStorage]);

  // Get product adoptions
  const productAdoptions = useMemo(() => {
    if (!useLocalStorage && backendData?.productAdoptions) {
      return backendData.productAdoptions;
    }
    return localProducts[storageKey] || {};
  }, [backendData, localProducts, storageKey, useLocalStorage]);

  // Get config completions
  const configCompletions = useMemo(() => {
    if (!useLocalStorage && backendData?.configCompletions) {
      return backendData.configCompletions;
    }
    return localConfigCompletions[storageKey] || {};
  }, [backendData, localConfigCompletions, storageKey, useLocalStorage]);

  // Get evidence
  const evidence = useMemo(() => {
    if (!useLocalStorage && backendData?.evidence) {
      return backendData.evidence;
    }
    return localEvidence[storageKey] || {};
  }, [backendData, localEvidence, storageKey, useLocalStorage]);

  // Get evidence checks
  const evidenceChecks = useMemo(() => {
    if (!useLocalStorage && backendData?.evidenceChecks) {
      return backendData.evidenceChecks;
    }
    return localEvidenceChecks[storageKey] || {};
  }, [backendData, localEvidenceChecks, storageKey, useLocalStorage]);

  // Sync localStorage when state changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(localAssessments));
    } catch (e) {
      console.warn('Failed to persist CISA assessments to localStorage:', e);
    }
  }, [localAssessments]);

  useEffect(() => {
    try {
      localStorage.setItem(TARGETS_STORAGE_KEY, JSON.stringify(localTargets));
    } catch (e) {
      console.warn('Failed to persist CISA targets to localStorage:', e);
    }
  }, [localTargets]);

  useEffect(() => {
    try {
      localStorage.setItem(PRODUCTS_STORAGE_KEY, JSON.stringify(localProducts));
    } catch (e) {
      console.warn('Failed to persist CISA products to localStorage:', e);
    }
  }, [localProducts]);

  useEffect(() => {
    try {
      localStorage.setItem(CONFIG_COMPLETIONS_STORAGE_KEY, JSON.stringify(localConfigCompletions));
    } catch (e) {
      console.warn('Failed to persist CISA config completions to localStorage:', e);
    }
  }, [localConfigCompletions]);

  useEffect(() => {
    try {
      localStorage.setItem(EVIDENCE_STORAGE_KEY, JSON.stringify(localEvidence));
    } catch (e) {
      console.warn('Failed to persist CISA evidence to localStorage:', e);
    }
  }, [localEvidence]);

  useEffect(() => {
    try {
      localStorage.setItem(EVIDENCE_CHECKS_STORAGE_KEY, JSON.stringify(localEvidenceChecks));
    } catch (e) {
      console.warn('Failed to persist CISA evidence checks to localStorage:', e);
    }
  }, [localEvidenceChecks]);

  /**
   * Update current level for a function
   * @param {string} functionCode - e.g., 'ID_AUTH'
   * @param {string} maturityLevel - e.g., 'INITIAL'
   */
  const updateCell = useCallback(
    (functionCode, maturityLevel) => {
      if (!useLocalStorage && departmentId) {
        updateMutation.mutate({
          functionCode,
          maturityLevel
        });
      }

      setLocalAssessments((prev) => ({
        ...prev,
        [storageKey]: {
          ...(prev[storageKey] || {}),
          [functionCode]: maturityLevel
        }
      }));
    },
    [departmentId, useLocalStorage, updateMutation, storageKey]
  );

  /**
   * Update target level for a function
   * @param {string} functionCode - e.g., 'ID_AUTH'
   * @param {string} targetLevel - e.g., 'ADVANCED'
   */
  const updateTargetLevel = useCallback(
    (functionCode, targetLevel) => {
      if (!useLocalStorage && departmentId) {
        updateMutation.mutate({
          functionCode,
          targetLevel
        });
      }

      setLocalTargets((prev) => ({
        ...prev,
        [storageKey]: {
          ...(prev[storageKey] || {}),
          [functionCode]: targetLevel
        }
      }));
    },
    [departmentId, useLocalStorage, updateMutation, storageKey]
  );

  /**
   * Adopt a product for a function
   * @param {string} functionCode - e.g., 'ID_AUTH'
   * @param {string} productName - e.g., 'YubiKey (FIDO2)'
   * @param {string} status - 'IN_USE' | 'PLANNED' | 'EVALUATING' | 'NOT_APPLICABLE'
   */
  const adoptProduct = useCallback(
    (functionCode, productName, status = 'PLANNED') => {
      const newProduct = {
        productName,
        status,
        adoptedAt: new Date().toISOString(),
        notes: null
      };

      if (!useLocalStorage && departmentId) {
        updateMutation.mutate({
          functionCode,
          adoptProduct: newProduct
        });
      }

      setLocalProducts((prev) => {
        const currentProducts = prev[storageKey]?.[functionCode] || [];
        // Don't add duplicates
        if (currentProducts.some(p => p.productName === productName)) {
          return prev;
        }
        return {
          ...prev,
          [storageKey]: {
            ...(prev[storageKey] || {}),
            [functionCode]: [...currentProducts, newProduct]
          }
        };
      });
    },
    [departmentId, useLocalStorage, updateMutation, storageKey]
  );

  /**
   * Update product status
   * @param {string} functionCode
   * @param {string} productName
   * @param {string} newStatus
   */
  const updateProductStatus = useCallback(
    (functionCode, productName, newStatus) => {
      if (!useLocalStorage && departmentId) {
        updateMutation.mutate({
          functionCode,
          updateProduct: { productName, status: newStatus }
        });
      }

      setLocalProducts((prev) => {
        const currentProducts = prev[storageKey]?.[functionCode] || [];
        return {
          ...prev,
          [storageKey]: {
            ...(prev[storageKey] || {}),
            [functionCode]: currentProducts.map(p =>
              p.productName === productName
                ? { ...p, status: newStatus }
                : p
            )
          }
        };
      });
    },
    [departmentId, useLocalStorage, updateMutation, storageKey]
  );

  /**
   * Remove a product from a function
   * @param {string} functionCode
   * @param {string} productName
   */
  const removeProduct = useCallback(
    (functionCode, productName) => {
      if (!useLocalStorage && departmentId) {
        updateMutation.mutate({
          functionCode,
          removeProduct: productName
        });
      }

      setLocalProducts((prev) => {
        const currentProducts = prev[storageKey]?.[functionCode] || [];
        return {
          ...prev,
          [storageKey]: {
            ...(prev[storageKey] || {}),
            [functionCode]: currentProducts.filter(p => p.productName !== productName)
          }
        };
      });
    },
    [departmentId, useLocalStorage, updateMutation, storageKey]
  );

  /**
   * Bulk update multiple assessments at once
   * @param {Object} updates - { functionCode: maturityLevel, ... }
   */
  const bulkUpdate = useCallback(
    (updates) => {
      if (!useLocalStorage && departmentId) {
        updateMutation.mutate({ bulk: updates });
      }

      setLocalAssessments((prev) => ({
        ...prev,
        [storageKey]: {
          ...(prev[storageKey] || {}),
          ...updates
        }
      }));
    },
    [departmentId, useLocalStorage, updateMutation, storageKey]
  );

  /**
   * Toggle a configuration step completion
   * @param {string} functionCode - e.g., 'ID_AUTH'
   * @param {string} level - e.g., 'ADVANCED'
   * @param {number} stepIndex - index of the step to toggle
   */
  const toggleConfigStep = useCallback(
    (functionCode, level, stepIndex) => {
      const key = `${functionCode}_${level}`;

      setLocalConfigCompletions((prev) => {
        const currentData = prev[storageKey] || {};
        const currentCompletion = currentData[key] || { completedSteps: [] };
        const completedSteps = [...currentCompletion.completedSteps];

        const existingIndex = completedSteps.indexOf(stepIndex);
        if (existingIndex >= 0) {
          completedSteps.splice(existingIndex, 1);
        } else {
          completedSteps.push(stepIndex);
        }

        return {
          ...prev,
          [storageKey]: {
            ...currentData,
            [key]: { completedSteps }
          }
        };
      });

      if (!useLocalStorage && departmentId) {
        updateMutation.mutate({
          functionCode,
          toggleConfigStep: { level, stepIndex }
        });
      }
    },
    [departmentId, useLocalStorage, updateMutation, storageKey]
  );

  /**
   * Add evidence for a function
   * @param {string} functionCode - e.g., 'ID_AUTH'
   * @param {Object} evidenceData - Evidence object to add
   */
  const addEvidence = useCallback(
    (functionCode, evidenceData) => {
      const newEvidence = {
        ...evidenceData,
        id: evidenceData.id || `${functionCode}_${Date.now()}`,
        createdAt: new Date().toISOString(),
        expiresAt: evidenceData.expiresAt || calculateExpirationDate(evidenceData.type)
      };

      if (!useLocalStorage && departmentId) {
        updateMutation.mutate({
          functionCode,
          addEvidence: newEvidence
        });
      }

      setLocalEvidence((prev) => {
        const currentEvidence = prev[storageKey]?.[functionCode] || [];
        return {
          ...prev,
          [storageKey]: {
            ...(prev[storageKey] || {}),
            [functionCode]: [...currentEvidence, newEvidence]
          }
        };
      });

      return newEvidence;
    },
    [departmentId, useLocalStorage, updateMutation, storageKey]
  );

  /**
   * Update existing evidence
   * @param {string} functionCode
   * @param {string} evidenceId
   * @param {Object} updates
   */
  const updateEvidence = useCallback(
    (functionCode, evidenceId, updates) => {
      if (!useLocalStorage && departmentId) {
        updateMutation.mutate({
          functionCode,
          updateEvidence: { evidenceId, updates }
        });
      }

      setLocalEvidence((prev) => {
        const currentEvidence = prev[storageKey]?.[functionCode] || [];
        return {
          ...prev,
          [storageKey]: {
            ...(prev[storageKey] || {}),
            [functionCode]: currentEvidence.map(e =>
              e.id === evidenceId ? { ...e, ...updates } : e
            )
          }
        };
      });
    },
    [departmentId, useLocalStorage, updateMutation, storageKey]
  );

  /**
   * Remove evidence
   * @param {string} functionCode
   * @param {string} evidenceId
   */
  const removeEvidence = useCallback(
    (functionCode, evidenceId) => {
      if (!useLocalStorage && departmentId) {
        updateMutation.mutate({
          functionCode,
          removeEvidence: evidenceId
        });
      }

      setLocalEvidence((prev) => {
        const currentEvidence = prev[storageKey]?.[functionCode] || [];
        return {
          ...prev,
          [storageKey]: {
            ...(prev[storageKey] || {}),
            [functionCode]: currentEvidence.filter(e => e.id !== evidenceId)
          }
        };
      });
    },
    [departmentId, useLocalStorage, updateMutation, storageKey]
  );

  /**
   * Update evidence check status
   * @param {string} checkId - e.g., 'ID_AUTH_INIT_01'
   * @param {Object} status - { status, notes, verifiedBy, evidenceIds }
   */
  const updateEvidenceCheck = useCallback(
    (checkId, status) => {
      const updatedCheck = {
        ...status,
        checkId,
        verifiedAt: status.status === 'VERIFIED' ? new Date().toISOString() : null
      };

      if (!useLocalStorage && departmentId) {
        updateMutation.mutate({
          updateEvidenceCheck: { checkId, status: updatedCheck }
        });
      }

      setLocalEvidenceChecks((prev) => ({
        ...prev,
        [storageKey]: {
          ...(prev[storageKey] || {}),
          [checkId]: updatedCheck
        }
      }));
    },
    [departmentId, useLocalStorage, updateMutation, storageKey]
  );

  /**
   * Bulk update evidence checks (e.g., for "verify all" action)
   * @param {Object} checks - { checkId: status, ... }
   */
  const bulkUpdateEvidenceChecks = useCallback(
    (checks) => {
      if (!useLocalStorage && departmentId) {
        updateMutation.mutate({
          bulkUpdateEvidenceChecks: checks
        });
      }

      setLocalEvidenceChecks((prev) => ({
        ...prev,
        [storageKey]: {
          ...(prev[storageKey] || {}),
          ...checks
        }
      }));
    },
    [departmentId, useLocalStorage, updateMutation, storageKey]
  );

  /**
   * Reset all assessments to TRADITIONAL
   */
  const resetAll = useCallback(() => {
    setLocalAssessments((prev) => ({
      ...prev,
      [storageKey]: {}
    }));
    setLocalTargets((prev) => ({
      ...prev,
      [storageKey]: {}
    }));
    setLocalProducts((prev) => ({
      ...prev,
      [storageKey]: {}
    }));
    setLocalConfigCompletions((prev) => ({
      ...prev,
      [storageKey]: {}
    }));
    setLocalEvidence((prev) => ({
      ...prev,
      [storageKey]: {}
    }));
    setLocalEvidenceChecks((prev) => ({
      ...prev,
      [storageKey]: {}
    }));

    if (!useLocalStorage && departmentId) {
      updateMutation.mutate({ reset: true });
    }
  }, [departmentId, useLocalStorage, updateMutation, storageKey]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    const pillarScores = {};
    let totalScore = 0;
    let totalFunctions = 0;

    // Calculate gap metrics
    let totalGaps = 0;
    let functionsWithGaps = 0;
    let totalProductsAdopted = 0;

    for (const pillar of CISA_PILLARS) {
      const score = calculatePillarScore(pillar.code, assessments);
      pillarScores[pillar.code] = score;
      totalScore += score;
      totalFunctions += 1;

      // Count gaps in this pillar
      const functions = CISA_FUNCTIONS[pillar.code] || [];
      for (const fn of functions) {
        const current = assessments[fn.code] || 'TRADITIONAL';
        const target = targetLevels[fn.code];
        if (target && target !== current) {
          const levels = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];
          const gap = levels.indexOf(target) - levels.indexOf(current);
          if (gap > 0) {
            totalGaps += gap;
            functionsWithGaps += 1;
          }
        }
        // Count products
        totalProductsAdopted += (productAdoptions[fn.code] || []).length;
      }
    }

    // Cross-cutting scores
    for (const cc of CROSS_CUTTING) {
      const level = assessments[cc.code] || 'TRADITIONAL';
      const scoreMap = { TRADITIONAL: 1, INITIAL: 2, ADVANCED: 3, OPTIMAL: 4 };
      pillarScores[cc.code] = scoreMap[level] || 1;

      // Count gaps for cross-cutting
      const target = targetLevels[cc.code];
      if (target && target !== level) {
        const levels = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];
        const gap = levels.indexOf(target) - levels.indexOf(level);
        if (gap > 0) {
          totalGaps += gap;
          functionsWithGaps += 1;
        }
      }
      totalProductsAdopted += (productAdoptions[cc.code] || []).length;
    }

    // Overall average across pillar scores
    const overallScore = totalFunctions > 0 ? totalScore / totalFunctions : 1;

    // Count functions at each level
    const levelCounts = { TRADITIONAL: 0, INITIAL: 0, ADVANCED: 0, OPTIMAL: 0 };
    const allFunctions = [
      ...Object.values(CISA_FUNCTIONS).flat(),
      ...CROSS_CUTTING
    ];

    for (const fn of allFunctions) {
      const level = assessments[fn.code] || 'TRADITIONAL';
      levelCounts[level] = (levelCounts[level] || 0) + 1;
    }

    return {
      pillarScores,
      overallScore,
      levelCounts,
      totalFunctions: allFunctions.length,
      // Gap metrics
      totalGaps,
      functionsWithGaps,
      totalProductsAdopted
    };
  }, [assessments, targetLevels, productAdoptions]);

  /**
   * Calculate three-layer derived scores for each function
   * This provides evidence-backed scoring transparency
   */
  const derivedScores = useMemo(() => {
    const scores = {};
    const allFunctions = [
      ...Object.values(CISA_FUNCTIONS).flat(),
      ...CROSS_CUTTING
    ];

    for (const fn of allFunctions) {
      const functionCode = fn.code;
      const currentLevel = assessments[functionCode] || 'TRADITIONAL';
      const fnProducts = productAdoptions[functionCode] || [];
      const fnEvidence = evidence[functionCode] || [];

      // 1. Calculate Capability Coverage (0-100)
      // Based on products adopted that are IN_USE or PLANNED
      let capabilityCoverage = 0;
      if (currentLevel !== 'TRADITIONAL') {
        const activeProducts = fnProducts.filter(
          p => p.status === 'IN_USE' || p.status === 'PLANNED'
        ).length;

        if (activeProducts === 0) {
          capabilityCoverage = 0;
        } else if (activeProducts === 1) {
          capabilityCoverage = 50;
        } else if (activeProducts === 2) {
          capabilityCoverage = 75;
        } else {
          capabilityCoverage = 100;
        }
      } else {
        capabilityCoverage = 100; // TRADITIONAL requires no products
      }

      // 2. Calculate Configuration Completeness (0-100)
      // Based on config steps completed for the current level
      let configurationCompleteness = 0;
      if (currentLevel !== 'TRADITIONAL') {
        const configKey = `${functionCode}_${currentLevel}`;
        const completionData = configCompletions[configKey];
        const configSteps = getConfigurationSteps(functionCode, currentLevel);
        const totalSteps = configSteps.length;
        const completedSteps = completionData?.completedSteps?.length || 0;

        if (totalSteps === 0) {
          configurationCompleteness = 100; // No config steps = fully configured
        } else {
          configurationCompleteness = Math.round((completedSteps / totalSteps) * 100);
        }
      } else {
        configurationCompleteness = 100; // TRADITIONAL requires no config
      }

      // 3. Calculate Evidence Confidence (0-100)
      // Based on evidence checks verified for the current level
      let evidenceConfidence = 0;
      if (currentLevel !== 'TRADITIONAL') {
        const levelChecks = getEvidenceChecksForLevel(functionCode, currentLevel);
        const requiredChecks = levelChecks.filter(c => c.type === 'REQUIRED');

        if (requiredChecks.length === 0) {
          evidenceConfidence = 100;
        } else {
          const verifiedCount = requiredChecks.filter(c => {
            const checkStatus = evidenceChecks[c.id];
            return checkStatus?.status === 'VERIFIED';
          }).length;
          evidenceConfidence = Math.round((verifiedCount / requiredChecks.length) * 100);
        }

        // Also factor in expired evidence
        const hasExpiredEvidence = fnEvidence.some(e => isEvidenceExpired(e));
        if (hasExpiredEvidence && evidenceConfidence > 0) {
          evidenceConfidence = Math.max(0, evidenceConfidence - 20); // Penalty for expired evidence
        }
      } else {
        evidenceConfidence = 100; // TRADITIONAL requires no evidence
      }

      // Calculate weighted score
      const weightedScore = Math.round(
        capabilityCoverage * SCORING_WEIGHTS.capabilityCoverage +
        configurationCompleteness * SCORING_WEIGHTS.configurationCompleteness +
        evidenceConfidence * SCORING_WEIGHTS.evidenceConfidence
      );

      // Derive level from weighted score
      let derivedLevel = 'TRADITIONAL';
      if (weightedScore >= LEVEL_THRESHOLDS.OPTIMAL) {
        derivedLevel = 'OPTIMAL';
      } else if (weightedScore >= LEVEL_THRESHOLDS.ADVANCED) {
        derivedLevel = 'ADVANCED';
      } else if (weightedScore >= LEVEL_THRESHOLDS.INITIAL) {
        derivedLevel = 'INITIAL';
      }

      // Check if manual override differs from derived
      const manualLevel = assessments[functionCode];
      const hasOverride = manualLevel && manualLevel !== derivedLevel;

      scores[functionCode] = {
        functionCode,
        capabilityCoverage,
        configurationCompleteness,
        evidenceConfidence,
        weightedScore,
        derivedLevel,
        manualLevel: manualLevel || 'TRADITIONAL',
        hasOverride,
        // Additional metadata
        productsCount: fnProducts.length,
        activeProductsCount: fnProducts.filter(p => p.status === 'IN_USE' || p.status === 'PLANNED').length,
        evidenceCount: fnEvidence.length,
        expiredEvidenceCount: fnEvidence.filter(e => isEvidenceExpired(e)).length,
        expiringEvidenceCount: fnEvidence.filter(e => getExpirationWarning(e.expiresAt) === 'expiring_soon').length
      };
    }

    return scores;
  }, [assessments, productAdoptions, configCompletions, evidence, evidenceChecks]);

  return {
    // Current level assessments (backward compatible)
    assessments,
    // Target levels
    targetLevels,
    // Product adoptions
    productAdoptions,
    // Configuration completions
    configCompletions,
    // Evidence items per function
    evidence,
    // Evidence check statuses per check ID
    evidenceChecks,
    // Three-layer derived scores per function
    derivedScores,
    // Summary stats
    summary,
    // Loading/saving states
    loading: isLoading,
    saving: updateMutation.isPending,
    error: queryError,
    // Actions for current levels
    updateCell,
    bulkUpdate,
    // Actions for targets
    updateTargetLevel,
    // Actions for products
    adoptProduct,
    updateProductStatus,
    removeProduct,
    // Actions for config completions
    toggleConfigStep,
    // Actions for evidence
    addEvidence,
    updateEvidence,
    removeEvidence,
    // Actions for evidence checks
    updateEvidenceCheck,
    bulkUpdateEvidenceChecks,
    // Reset
    resetAll,
    // Storage mode
    useLocalStorage
  };
}

export default useCisaAssessment;
